/**
 * Error Parser Utility
 *
 * Provides utilities to parse both new structured error format and legacy error formats
 * from the backend API, ensuring backward compatibility.
 */

export interface ParsedError {
  code: string;
  message: string;
  entity?: string;
  details?: {
    reason?: string;
    field?: string;
    attempts?: number;
    [key: string]: any;
  };
}

/**
 * Parse API error response into a standardized format.
 * Supports both new structured format and legacy formats.
 *
 * @param error - The error object from HTTP request
 * @returns ParsedError object or null if unparseable
 */
export function parseApiError(error: any): ParsedError | null {
  // Handle new structured format
  // { error: { code: "...", message: "...", entity: "...", details: {...} } }
  if (error?.error?.error?.code) {
    return {
      code: error.error.error.code,
      message: error.error.error.message || '',
      entity: error.error.error.entity,
      details: error.error.error.details,
    };
  }

  // Handle old format with "error" field as code
  // { error: { error: "ERROR_CODE", message: "..." } }
  if (error?.error?.error && typeof error.error.error === 'string') {
    return {
      code: error.error.error,
      message: error.error.message || '',
    };
  }

  // Handle old "detail" format (validation errors, etc.)
  // { error: { detail: "Error message" } }
  if (error?.error?.detail) {
    return {
      code: 'UNKNOWN_ERROR',
      message: error.error.detail,
    };
  }

  return null;
}

/**
 * Extract the most user-friendly message from a parsed error.
 * Prioritizes detailed reason from new format, falls back to main message.
 *
 * @param parsedError - The parsed error object
 * @returns User-friendly error message
 */
export function getUserFriendlyMessage(parsedError: ParsedError): string {
  // Prioritize detailed reason from new format
  if (parsedError.details?.reason) {
    return parsedError.details.reason;
  }

  // Fall back to main message
  return parsedError.message || 'An unexpected error occurred';
}

/**
 * Build a complete error message including entity prefix if available.
 *
 * @param parsedError - The parsed error object
 * @param customMessage - Optional custom message to use instead of default
 * @returns Complete error message with entity prefix if applicable
 */
export function buildErrorMessage(
  parsedError: ParsedError,
  customMessage?: string,
): string {
  const entityPrefix = parsedError.entity ? `${parsedError.entity}: ` : '';
  const message = customMessage || getUserFriendlyMessage(parsedError);
  return `${entityPrefix}${message}`;
}
